import { supabase } from "@/integrations/supabase/client";
import { Message } from "@/types/message";
import { getIsCacheEnabled } from "@/utils/cacheSettings";
import { debugLogger } from "@/utils/debugLogger";
import { CachedResponse } from "./types";
import { normalizeQuestion, shouldExcludeFromCache } from "./questionNormalizer";
import { createSmartCacheKey } from "./cacheKeyGenerator";
import { isFollowUpQuestion } from "./followUpDetector";
import { createContextHash } from "./contextHasher";

export const checkChatCache = async (
  content: string,
  hotelId: string | undefined,
  messages: Message[] = []
): Promise<CachedResponse | null> => {
  // First check if cache is enabled at all
  if (!getIsCacheEnabled()) return null;
  
  // Then check if we have a hotelId to query with
  if (!hotelId) return null;

  // Check if this question should be excluded from caching
  if (shouldExcludeFromCache(normalizeQuestion(content))) {
    debugLogger.log(`🚫 Excluding context-dependent question from cache: ${content}`);
    return null;
  }

  const smartCacheKey = createSmartCacheKey(content, hotelId, messages);
  const isFollowUp = isFollowUpQuestion(content, messages);
  const contextHash = createContextHash(messages, content);
  
  debugLogger.log(`🔍 Smart cache lookup - Key: ${smartCacheKey}, IsFollowUp: ${isFollowUp}`);
  
  // Check if this smart cache key exists
  const { data: cachedData, error: cacheError } = await supabase
    .from('cached_chats')
    .select('answer, model, token_count, normalized_question')
    .eq('hotel_id', hotelId)
    .eq('normalized_question', smartCacheKey)
    .maybeSingle();
  
  if (cacheError) {
    debugLogger.error("❌ Smart cache lookup error:", cacheError);
    return null;
  }
  
  // If we found a cached answer, use it
  if (cachedData && cachedData.answer) {
    debugLogger.log(`✅ Smart cache hit! Using cached answer for: ${content}`);
    
    return {
      message: cachedData.answer,
      cached: true,
      model: cachedData.model || 'unknown',
      tokenCount: cachedData.token_count || 0,
      statusCode: 200,
      normalizedQuestion: cachedData.normalized_question,
      contextHash,
      isFollowUp
    };
  }
  
  debugLogger.log(`❌ Smart cache miss for: ${content}`);
  return null;
};

export const storeChatInCache = async (
  hotelId: string | undefined,
  content: string,
  response: any,
  modelName: string | undefined,
  messages: Message[] = []
) => {
  // Check if cache is enabled and if we have both a hotelId and a valid response
  if (!getIsCacheEnabled() || !hotelId || !response.message || response.error) return;
  
  // Check if this question should be excluded from caching
  if (shouldExcludeFromCache(normalizeQuestion(content))) {
    debugLogger.log(`🚫 Excluding context-dependent question from storage: ${content}`);
    return;
  }
  
  const smartCacheKey = createSmartCacheKey(content, hotelId, messages);
  const isFollowUp = isFollowUpQuestion(content, messages);
  const contextHash = createContextHash(messages, content);
  
  debugLogger.log(`💾 Smart cache store - Key: ${smartCacheKey}, IsFollowUp: ${isFollowUp}`);
  
  const { error: insertError } = await supabase
    .from('cached_chats')
    .insert({
      hotel_id: hotelId,
      question: content,
      normalized_question: smartCacheKey, // Store the smart cache key
      answer: response.message,
      model: modelName || 'gpt-4o-mini',
      token_count: response.tokenCount || 0,
      is_cached: false,
      context_hash: contextHash,
      is_follow_up: isFollowUp,
      conversation_context: messages.slice(-3).map(msg => ({
        isBot: msg.isBot,
        content: msg.content.substring(0, 200), // Store truncated content
        timestamp: msg.timestamp.toISOString() // Convert Date to string for JSON storage
      }))
    });
    
  if (insertError) {
    debugLogger.error("❌ Error storing smart cache:", insertError);
  } else {
    debugLogger.log(`✅ Smart cache stored successfully for: ${content}`);
  }
};